import { useEffect, useState } from "react";
import { MaintenanceState } from "@badorbad/shared";
import { useGame } from "../stores/game";

/**
 * Maintenance as the client should act on it *now*.
 *
 * The server sends an absolute instant and then says nothing more until the
 * state changes again, so the promotion from `Draining` to `Active` happens
 * here, against the local clock — exactly as it happens server-side in
 * `maintenance/state.ts`. Both sides reach the same conclusion from the same
 * stored instant, and neither needs a timer to tell the other.
 *
 * The client's copy is only ever an affordance: it dims a start button and
 * shows a banner. Every rule is enforced again on the server, so a clock that
 * disagrees by a few seconds costs nothing.
 */
export interface Maintenance {
  state: MaintenanceState;
  /** Milliseconds until the drain ends. Zero unless draining. */
  remainingMs: number;
}

const OFF: Maintenance = { state: MaintenanceState.Off, remainingMs: 0 };

export const useMaintenance = (): Maintenance => {
  const payload = useGame((s) => s.maintenance);
  const [now, setNow] = useState(() => Date.now());

  const counting =
    payload?.state === MaintenanceState.Draining && payload.until !== null;

  useEffect(() => {
    if (!counting) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [counting]);

  if (!payload || payload.state === MaintenanceState.Off) return OFF;

  if (payload.state === MaintenanceState.Draining && payload.until !== null) {
    const remainingMs = new Date(payload.until).getTime() - now;
    return remainingMs > 0
      ? { state: MaintenanceState.Draining, remainingMs }
      : { state: MaintenanceState.Active, remainingMs: 0 };
  }

  return { state: payload.state, remainingMs: 0 };
};
